import { SagaIterator } from 'redux-saga';
import {
  all,
  select,
  take,
  fork,
  delay,
  put,
  call,
  race,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import * as Cookie from 'js-cookie';
import { PublicSessionState } from '@wix/da-react-context/pkg/publicSession';
import { requestPuppy, isApiError } from '@wix/da-http-client';
import { appMounted } from '../app/actions';
import {
  setPushSubscriptionState,
  togglePushSubscription,
  registerPushSubscription,
  unregisterPushSubscription,
  initPushSubscriptions,
  initPushSubscriptionsSuccess,
  updatePushSubscriptions,
  displayPushNotificationsModal,
  dismissPWABanner,
  showPWABanner,
} from './actions';
import { PushSubscriptionState, PushSubscriptions } from './types';
import { getIsMobile, getPublicSession } from '../../publicSession/selectors';
import {
  confirmNo,
  confirmYes,
  pushModal,
  pushGlobalConfirmationModal,
} from '../../Modals/redux/actionCreators';
import { ModalType } from '../../Modals/redux/types';
import {
  PapiDaSharedPushRegister,
  PapiDaSharedPushSettings,
  PapiDaSharedPushSettingsUpdate,
  PapiRequestDaSharedPushRegister,
  PapiRequestDaSharedPushSettingsUpdate,
} from '@wix/da-papi-types';
import {
  getArePushSubscriptionsInitialized,
  getPushSubscriptionState,
  getPushSubscriptions,
} from './selectors';
import { isiOS } from '../../utils/browser';
import { PWA_COOKIE_NAME, PWA_COOKIE_VALUE } from './constants';

let registration: ServiceWorkerRegistration | null = null;

function* registerPush(
  endpoint_url: string,
  public_key: string,
  auth_secret: string
) {
  const params: PapiRequestDaSharedPushRegister = {
    endpoint_url,
    public_key,
    auth_secret,
  };
  const result: PapiDaSharedPushRegister | false = yield call(requestPuppy, {
    method: 'post',
    url: '/push/register',
    data: params,
  });
  if (!result || isApiError(result)) {
    return false;
  }
  return result.success;
}

function* unregisterPush() {
  yield call(requestPuppy, {
    method: 'post',
    url: '/push/unregister',
  });
}

function* getNotificationPermission() {
  if (!registration) {
    return false;
  }

  // we only care to adjust permission when it's default and only on iOS
  if (window.Notification.permission !== 'default' || !isiOS()) {
    return window.Notification.permission;
  }

  // there's a possibility on iOS that user granted permission, but quit the app and permission got reset to "default"
  // it's a false "default" and will be reset to granted later
  const subscription: PushSubscription | null =
    yield registration.pushManager.getSubscription();

  if (subscription) {
    ddt.log('pwa', 'Overriding permission from "default" to "granted"');
    return 'granted';
  }

  return 'default';
}

function askNotificationPermission() {
  return new Promise(function (resolve, reject) {
    const permissionResult = Notification.requestPermission(function (result) {
      resolve(result);
    });
    try {
      permissionResult.then(resolve, reject);
    } catch (error) {
      if (error instanceof TypeError) {
        // Safari uses a deprecated callback version of Notification.requestPermission
        // https://developer.mozilla.org/en-US/docs/Web/API/Notification/requestPermission_static#syntax
        Notification.requestPermission(resolve);
      } else {
        ddt.log('pwa', 'Error thrown for push notifications permission');
        throw error;
      }
    }
  });
}

function* subscribeToPush() {
  if (!registration) {
    return;
  }
  const session: PublicSessionState = yield select(getPublicSession);
  try {
    const subscription: PushSubscription =
      yield registration.pushManager.subscribe({
        applicationServerKey: session.push.vapidPublicKey!,
        userVisibleOnly: true,
      });
    const json = subscription.toJSON();
    const subscribed = yield call(
      registerPush,
      json.endpoint ?? '',
      json.keys?.p256dh ?? '',
      json.keys?.auth ?? ''
    );
    if (subscribed) {
      yield put(setPushSubscriptionState(PushSubscriptionState.SUBSCRIBED));
      ddt.log('pwa', 'Subscribed successfully to push notifications');
    } else {
      yield put(setPushSubscriptionState(PushSubscriptionState.FAILED));
    }
  } catch (err) {
    ddt.error('pwa', 'Failed to subscribe to push notifications', err);
    yield put(setPushSubscriptionState(PushSubscriptionState.FAILED));
  }
}

function* initPushSubscriptionState() {
  try {
    if (!registration || typeof window === 'undefined') {
      return;
    }

    // IOS only has this when installed to the homescreen
    if (!('Notification' in window)) {
      yield put(
        setPushSubscriptionState(PushSubscriptionState.UNABLE_TO_SUBSCRIBE)
      );
      return;
    }

    if (window.Notification.permission === 'denied') {
      yield put(
        setPushSubscriptionState(PushSubscriptionState.UNABLE_TO_SUBSCRIBE)
      );
      ddt.log('pwa', 'Init cancelled due to permission denied');
      return;
    }

    const truePermission = yield call(getNotificationPermission);

    if (truePermission === 'default') {
      yield put(
        setPushSubscriptionState(PushSubscriptionState.READY_TO_SUBSCRIBE)
      );
      return;
    }

    if (truePermission !== 'granted') {
      ddt.log('pwa', 'exiting due to permission not being "granted"');
      return;
    }

    const subscription: PushSubscription | null =
      yield registration.pushManager.getSubscription();

    const session: PublicSessionState = yield select(getPublicSession);
    if (subscription) {
      // If the device subscription doesn't match backend record
      // unsubscribe and, if necessary, resubscribe again.
      if (subscription.endpoint === session.push.endpoint) {
        ddt.log('pwa', 'Valid existing subscription found.');
        yield put(setPushSubscriptionState(PushSubscriptionState.SUBSCRIBED));
      } else {
        ddt.log('pwa', 'Resubscribing to push notifications');
        yield subscription.unsubscribe();
        yield call(subscribeToPush);
      }
    } else {
      if (session.push.endpoint) {
        ddt.log(
          'pwa',
          'Existing subscription does not match server subscription. Unregistering.'
        );
        yield call(unregisterPush);
      }
      yield put(
        setPushSubscriptionState(PushSubscriptionState.READY_TO_SUBSCRIBE)
      );
    }
  } catch (e) {
    ddt.error('pwa', 'Failed to init push', e);
  }
}

function* showConfirmationModal() {
  // Confirm Subscription
  yield put(
    pushGlobalConfirmationModal({
      isFullscreen: false,
      titleTransKey: 'pwa.confirmationPopup.title',
      textTransKey: 'pwa.confirmationPopup.text',
      confirmBtnLabelTransKey: 'pwa.confirmationPopup.button.cta',
      cancelBtnLabelTransKey: 'pwa.confirmationPopup.button.cancel',
    })
  );
  const { yes } = yield race({
    yes: take(confirmYes),
    no: take(confirmNo),
  });
  return yes;
}

function* showPushNotificationsModal() {
  const truePermission = yield call(getNotificationPermission);

  // if user granted permission but quit the PWA app on iOS, permission sometimes gets reset to "default"
  // so we check here for a mismatch (present only on iOS)
  // if found, we request permission again to set it to "granted"
  // this is only possible within a user-initiated event like a click such as this one
  // note: it will be auto-granted by system with no prompt shown since user granted it before
  if (truePermission !== window.Notification.permission) {
    askNotificationPermission();
  }

  const initialized = yield select(getArePushSubscriptionsInitialized);
  if (!initialized) {
    yield put(initPushSubscriptions());
  }

  yield put(
    pushModal(ModalType.PUSH_NOTIFICATIONS, {
      wrapInModal: false,
    })
  );
}

function* checkIfPushSubscriptionsReady() {
  if (!registration) {
    return false;
  }
  const state: PushSubscriptionState = yield select(getPushSubscriptionState);
  if (
    [
      PushSubscriptionState.LOADING,
      PushSubscriptionState.FAILED,
      PushSubscriptionState.UNABLE_TO_SUBSCRIBE,
    ].includes(state)
  ) {
    ddt.log('pwa', 'push subscriptions are not ready', state);
    return false;
  }

  return true;
}

function* handleRegisterPush(
  action: ReturnType<typeof registerPushSubscription>
) {
  try {
    const isReady = checkIfPushSubscriptionsReady();
    if (!isReady) {
      return;
    }

    const state: PushSubscriptionState = yield select(getPushSubscriptionState);

    // we only care about READY_TO_SUBSCRIBE after this point
    if (state !== PushSubscriptionState.READY_TO_SUBSCRIBE) {
      ddt.log(
        'pwa',
        'exiting registration due to push subscriptions state not READY_TO_SUBSCRIBE'
      );
      return;
    }

    yield put(setPushSubscriptionState(PushSubscriptionState.LOADING));
    const truePermission = yield call(getNotificationPermission);
    // start subscribing
    if (truePermission === 'default') {
      // we prompt the user with a confirmation modal first to avoid accidental
      // push notifications blocking
      const confirmed = yield call(showConfirmationModal);
      if (!confirmed) {
        yield put(
          setPushSubscriptionState(PushSubscriptionState.READY_TO_SUBSCRIBE)
        );
        return;
      }
      const permission: NotificationPermission = yield call(
        askNotificationPermission
      );
      if (permission !== 'granted') {
        yield put(
          setPushSubscriptionState(PushSubscriptionState.UNABLE_TO_SUBSCRIBE)
        );
        ddt.log('pwa', 'Permission denied for push notifications');
        return;
      }
    }

    // actually subscribe to the push notifications
    yield call(subscribeToPush);

    // user has interacted with the push notifications, hide the banner
    yield put(dismissPWABanner());
  } catch (e) {
    ddt.error('pwa', 'Failed to register push', e);
  }
}

function* handleUnregisterPush(
  action: ReturnType<typeof unregisterPushSubscription>
) {
  try {
    const isReady = checkIfPushSubscriptionsReady();
    if (!isReady) {
      return;
    }

    // Only unsubscribe if the state is SUBSCRIBED
    const state: PushSubscriptionState = yield select(getPushSubscriptionState);
    if (state !== PushSubscriptionState.SUBSCRIBED) {
      ddt.log('pwa', 'exiting unregister due to state not SUBSCRIBED');
      return;
    }

    yield put(setPushSubscriptionState(PushSubscriptionState.LOADING));
    const subscription: PushSubscription | null =
      yield registration?.pushManager?.getSubscription();
    if (subscription) {
      ddt.log('pwa', 'unregistering push subscriptions');
      // unsubscribe
      yield subscription.unsubscribe();
      yield call(unregisterPush);
    }

    yield put(
      setPushSubscriptionState(PushSubscriptionState.READY_TO_SUBSCRIBE)
    );

    // user has interacted with the push notifications, hide the banner
    yield put(dismissPWABanner());

    ddt.log('pwa', 'successfully unregistered push subscriptions');
  } catch (e) {
    ddt.error('pwa', 'Failed to unregister push', e);
  }
}

function* handleTogglePush(action: ReturnType<typeof togglePushSubscription>) {
  const state: PushSubscriptionState = yield select(getPushSubscriptionState);
  const isReady = checkIfPushSubscriptionsReady();
  if (!isReady) {
    return;
  }
  if (state === PushSubscriptionState.SUBSCRIBED) {
    ddt.log('pwa', 'toggling push subscriptions off');
    yield put(unregisterPushSubscription());
  } else {
    ddt.log('pwa', 'toggling push subscriptions on');
    yield put(registerPushSubscription());
  }
}

function* handleInitPushSubscriptions(
  action: ReturnType<typeof initPushSubscriptions>
) {
  const result: PapiDaSharedPushSettings | false = yield call(requestPuppy, {
    method: 'get',
    url: '/push/settings',
  });
  if (!result || isApiError(result)) {
    ddt.error('pwa', 'Failed to fetch push subscriptions');
    return false;
  }
  ddt.log('pwa', 'push subscription settings initialized', result.send);
  yield put(initPushSubscriptionsSuccess(result.send));
}

function* handleUpdatePushSubscriptions(
  action: ReturnType<typeof updatePushSubscriptions>
) {
  yield delay(1000); // + takeLatest = debounced
  const { payload } = action;
  const subscriptions = yield select(getPushSubscriptions);
  const {
    note,
    commentReply,
    watcher,
    mention,
    favourite,
    favouriteMilestone,
  } = { ...subscriptions, ...payload } as PushSubscriptions;

  const data: PapiRequestDaSharedPushSettingsUpdate = {
    send: {
      note,
      watcher,
      mention,
      favourite,
      comment_reply: commentReply,
      favourite_milestone: favouriteMilestone,
    },
  };
  const result: PapiDaSharedPushSettingsUpdate | false = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/push/settings/update',
      data,
    }
  );

  if (!result || isApiError(result)) {
    ddt.error('pwa', 'Failed to update notification subscriptions');
    return false;
  } else {
    ddt.log('pwa', 'Updated notifications settings', result);
  }
}

function* handleAppMounted() {
  // wait for mount before doing anything
  yield take(appMounted);

  if (typeof window === 'undefined') {
    return;
  }

  try {
    const isMobile = yield select(getIsMobile);
    let pwaDebugDesktop = false;
    try {
      // Dev-only desktop override for PWA registration.
      // Enable: localStorage.setItem('pwa_debug_desktop', '1')
      // Disable: localStorage.removeItem('pwa_debug_desktop')
      pwaDebugDesktop = window.localStorage.getItem('pwa_debug_desktop') === '1';
    } catch (e) {
      ddt.error('pwa', 'Unable to read pwa_debug_desktop override', e);
    }

    const shouldPWA = isMobile || isiOS() || pwaDebugDesktop;

    // Give the page some time to settle
    yield delay(1000);

    if ('serviceWorker' in navigator) {
      if (shouldPWA) {
        ddt.log('pwa', 'Registering PWA service worker');
        registration = yield call(
          [navigator.serviceWorker, 'register'],
          '/sw.js',
          {
            scope: '/',
          }
        );
        yield call(initPushSubscriptionState);
        if (Cookie.get(PWA_COOKIE_NAME) !== PWA_COOKIE_VALUE) {
          yield put(showPWABanner());
        }
      } else {
        navigator.serviceWorker
          .getRegistrations()
          .then(function (registrations) {
            for (const reg of registrations) {
              ddt.log('pwa', 'Found service worker', reg.active?.scriptURL);
              if (reg.active?.scriptURL) {
                const url = new URL(reg.active?.scriptURL);
                if (url.pathname === '/sw.js') {
                  ddt.log(
                    'pwa',
                    'Unregistering service worker',
                    reg.active?.scriptURL
                  );
                  reg.unregister();
                }
              }
            }
          });
      }
    } else {
      yield put(
        setPushSubscriptionState(PushSubscriptionState.UNABLE_TO_SUBSCRIBE)
      );
      ddt.log('pwa', 'Service worker API is not supported');
    }
  } catch (e) {
    ddt.error('pwa', 'Failed to PWA', e);
  }
}

function* handleDismissPWABanner() {
  Cookie.set(PWA_COOKIE_NAME, PWA_COOKIE_VALUE, { expires: 30 });
}

export default function* saga(): SagaIterator {
  yield all([
    fork(handleAppMounted),
    takeLatest(togglePushSubscription, handleTogglePush),
    takeLatest(registerPushSubscription, handleRegisterPush),
    takeLatest(unregisterPushSubscription, handleUnregisterPush),
    takeEvery(initPushSubscriptions, handleInitPushSubscriptions),
    takeEvery(displayPushNotificationsModal, showPushNotificationsModal),
    takeEvery(dismissPWABanner, handleDismissPWABanner),
    takeLatest(updatePushSubscriptions, handleUpdatePushSubscriptions), // debounced
  ]);
}
